import { UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { HTTPError } from 'ky';

import { queryClient } from '@/adapters/query';
import { crmScreenerApiClient } from '@/adapters/schemaClients';
import {
  CreateFilterViewOperationRequest,
  DeleteFilterViewByIdRequest,
  FilterView,
  FindAllFilterViewsRequest,
  UpdateFilterViewByIdRequest
} from '@/types/crm-screener-api';

export const FIND_ALL_SCREENER_FILTER_VIEWS_QUERY_KEY =
  'findAllScreenerFilterViews';

export const SCREENER_FILTER_VIEW_KEY_ALL_LEADS = 'allLeads';

export const SCREENER_FILTER_VIEW_KEY_PAL_AUTHORIZATIONS = 'palAuthorizations';

export const SCREENER_FILTER_VIEW_KEY_PAL_REAUTHS = 'palReauths';

export const SCREENER_FILTER_VIEW_KEY_ALL_RESIDENTS = 'allResidents';

export const invalidateScreenerFilterViewsQueries = () => {
  queryClient.invalidateQueries([FIND_ALL_SCREENER_FILTER_VIEWS_QUERY_KEY]);
};

export const useScreenerFilterViewsQuery = (
  params: FindAllFilterViewsRequest & { currentUserId?: string },
  options: UseQueryOptions<FilterView[], HTTPError> = {}
) => {
  const { currentUserId, ...apiParams } = params;
  const isEnabled =
    !!apiParams.scope &&
    !!apiParams.viewKey &&
    !!currentUserId &&
    (options.enabled ?? true);

  const query = useQuery<FilterView[], HTTPError>(
    [FIND_ALL_SCREENER_FILTER_VIEWS_QUERY_KEY, currentUserId, apiParams],
    async () => {
      const response = await crmScreenerApiClient.findAllFilterViews(apiParams);
      return response.data ?? [];
    },
    {
      ...options,
      enabled: isEnabled
    }
  );

  const create = useMutation(
    (createParams: CreateFilterViewOperationRequest) => {
      return crmScreenerApiClient.createFilterView(createParams);
    },
    {
      onSuccess: invalidateScreenerFilterViewsQueries
    }
  );

  const update = useMutation(
    (updateParams: UpdateFilterViewByIdRequest) => {
      return crmScreenerApiClient.updateFilterViewById(updateParams);
    },
    {
      onSuccess: invalidateScreenerFilterViewsQueries
    }
  );

  const deleteMutation = useMutation(
    (deleteParams: DeleteFilterViewByIdRequest) => {
      return crmScreenerApiClient.deleteFilterViewById(deleteParams);
    },
    {
      onSuccess: invalidateScreenerFilterViewsQueries
    }
  );

  return {
    ...query,
    data: query.data ?? [],
    invalidateScreenerFilterViewsQueries,
    mutations: {
      create,
      update,
      delete: deleteMutation
    }
  };
};
